import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Landmark, Save, Loader2, Upload, FileSpreadsheet, CheckCircle2, XCircle, Info,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const previewRemarks = (template) => {
  const now = new Date();
  const mmm = now.toLocaleString("en", { month: "short" }).toUpperCase();
  const yy = String(now.getFullYear()).slice(-2);
  return (template || "").replace("{MMM}", mmm).replace("{YY}", yy);
};

export default function CompanyBanking() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [templateInfo, setTemplateInfo] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const fetchAll = async () => {
    try {
      const [sRes, tRes] = await Promise.all([
        axios.get(`${API}/company-bank-settings`),
        axios.get(`${API}/company-bank-settings/blkpay-template/info`),
      ]);
      setSettings(sRes.data);
      setTemplateInfo(tRes.data);
    } catch (e) { toast.error("Failed to load company banking settings"); }
  };

  useEffect(() => { fetchAll(); }, []);

  const save = async () => {
    if (!settings.debit_account_number?.trim()) { toast.error("Company Debit Account Number is required"); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/company-bank-settings`, settings);
      toast.success("Company banking settings saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const uploadTemplate = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/company-bank-settings/blkpay-template`, fd);
      toast.success(`Template accepted — data rows start at row ${res.data.data_start_row}`);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Template upload failed"); }
    finally { setUploading(false); }
  };

  if (!settings) return <div className="p-6 flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;

  const set = (k, v) => setSettings((p) => ({ ...p, [k]: v }));

  return (
    <div className="p-4 sm:p-6 max-w-4xl" data-testid="company-banking-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>Company Banking</h1>
        <p className="text-sm text-slate-500 mt-0.5">Debit account, defaults and the BLKPAY master template — these auto-populate every bank export</p>
      </div>

      {/* Settings form */}
      <div className="bg-white rounded-xl border p-5 mb-6">
        <h2 className="font-semibold text-slate-800 mb-4 flex items-center gap-2"><Landmark className="w-4 h-4" />Payment Defaults</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs">Company Debit Account Number *</Label>
            <Input value={settings.debit_account_number || ""} onChange={(e) => set("debit_account_number", e.target.value)} data-testid="debit-account-input" />
            <p className="text-[11px] text-slate-400 mt-1">The company account payments are debited from</p>
          </div>
          <div>
            <Label className="text-xs">Default Transaction Type</Label>
            <Select value={settings.transaction_type || "NEFT"} onValueChange={(v) => set("transaction_type", v)}>
              <SelectTrigger data-testid="transaction-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="NEFT">NEFT — Inter-bank</SelectItem>
                <SelectItem value="RTGS">RTGS — Inter-bank (high value)</SelectItem>
                <SelectItem value="IFT">IFT — Within bank</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Default Currency</Label>
            <Input value={settings.currency || "INR"} onChange={(e) => set("currency", e.target.value)} data-testid="currency-input" />
          </div>
          <div>
            <Label className="text-xs">Default Remarks Template</Label>
            <Input value={settings.remarks_template || ""} onChange={(e) => set("remarks_template", e.target.value)} data-testid="remarks-template-input" />
            <p className="text-[11px] text-slate-400 mt-1">
              {"{MMM} = month, {YY} = year → "}<span className="font-mono text-slate-600">{previewRemarks(settings.remarks_template)}</span>
              {previewRemarks(settings.remarks_template).length > 35 && <span className="text-rose-500 ml-1">exceeds 35 chars!</span>}
            </p>
          </div>
        </div>

        <h3 className="font-medium text-slate-700 mt-5 mb-3 text-sm">Custom Headers (Credit Advice — optional)</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {[1, 2, 3, 4, 5].map((n) => (
            <div key={n}>
              <Label className="text-xs">Custom Header {n}</Label>
              <Input value={settings[`custom_header_${n}`] || ""} onChange={(e) => set(`custom_header_${n}`, e.target.value)} data-testid={`custom-header-${n}`} />
            </div>
          ))}
        </div>

        <div className="mt-5 flex justify-end">
          <Button className="bg-slate-900 hover:bg-slate-800" disabled={saving} onClick={save} data-testid="save-banking-settings-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}Save Settings
          </Button>
        </div>
      </div>

      {/* Template management */}
      <div className="bg-white rounded-xl border p-5">
        <h2 className="font-semibold text-slate-800 mb-1 flex items-center gap-2"><FileSpreadsheet className="w-4 h-4 text-emerald-600" />BLKPAY Master Template</h2>
        <p className="text-xs text-slate-500 mb-4">Exports open this exact file and only inject payment rows — formatting, formulas, hidden sheets and merged cells are always preserved. Upload a new template anytime the bank format changes; no code change needed.</p>

        {templateInfo?.exists && templateInfo?.valid ? (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-4 text-sm" data-testid="template-info">
            <p className="flex items-center gap-2 text-emerald-700 font-medium"><CheckCircle2 className="w-4 h-4" />Template active</p>
            <div className="mt-1.5 text-xs text-slate-600 space-y-0.5">
              <p>Sheet: <b>{templateInfo.sheet}</b> · Header row {templateInfo.header_row} · Payments injected from row <b>{templateInfo.data_start_row}</b></p>
              <p>Sheets in workbook: {templateInfo.all_sheets?.join(", ")} · {templateInfo.size_kb} KB</p>
              <p>Last uploaded: {String(templateInfo.uploaded_at || "").slice(0, 16).replace("T", " ")} UTC</p>
            </div>
          </div>
        ) : templateInfo?.exists ? (
          <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 mb-4 text-sm text-rose-700" data-testid="template-invalid">
            <p className="flex items-center gap-2"><XCircle className="w-4 h-4" />Template invalid: {templateInfo.error}</p>
          </div>
        ) : (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-sm text-amber-700" data-testid="template-missing">
            <p className="flex items-center gap-2"><Info className="w-4 h-4" />No template uploaded yet — BLKPAY exports are disabled until one is uploaded.</p>
          </div>
        )}

        <input ref={fileRef} type="file" accept=".xlsx" className="hidden" onChange={(e) => uploadTemplate(e.target.files?.[0])} data-testid="template-file-input" />
        <Button variant="outline" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid="upload-template-btn">
          {uploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
          {templateInfo?.exists ? "Replace Template" : "Upload Template"}
        </Button>
        <Badge variant="outline" className="ml-3 text-xs text-slate-500">Must contain a "Beneficiary Name" header column</Badge>
      </div>
    </div>
  );
}
